import { type ARTICLE_SUB_ITEM_VALUE, articleSupportsSubItem, validateSubItem } from "@/model/article-sub-items"
import type { Article } from "@/model/backend/article"
import { isLiquidArticleType } from "@/model/liquid-types"
import type { ArticleSubItemType } from "@/model/backend/article-sub-item-type"
import { useHttpClient } from "@/vf"
import type { AxiosResponse } from "axios"
import { defineStore } from "pinia"
import { computed, reactive, ref, watch } from "vue"
import { type ShopAppConfig, useShopAppConfig } from "../composables"

type StockStatus = "not-in-stock" | "not-enough-stock" | "batch-overflow" | "ok"

export interface CartItem {
    article: Article
    amount: number
    singlePrice?: number
    totalPrice?: number
    originalPrice?: number
    originalTotalPrice?: number
    tax?: number
    /** deprecated use stockStatus instead */
    // inStock?: boolean
    /** Can be undefined for special cart items (fees, shipping costs etc.) */
    stockStatus: StockStatus | undefined

    // packaging, humidity etc
    subItems?: CartItemSubitem[]

    // the total amount for all sub items, calculated by the server
    subItemsTotal?: number
}

export interface CartItemSubitem {
    type: ArticleSubItemType
    value: ARTICLE_SUB_ITEM_VALUE
}

// the item that the server sends us (CartController)
interface ServerItem {
    id: string // article id
    name: string
    amount: number
    singlePrice?: number
    totalPrice?: number
    type:
        | "cannabis"
        | "standard"
        | "merch"
        | "essence"
        | "xtracts"
        | "readymade"
        | "milled"
        | "smalls"
        | "fee"
        | "shipping"
        | "discount"
        | "balance"
        | "coupon"
        | "custom"
    packageAmount: number
    tax: number
    stockStatus: StockStatus | undefined
    subItemsTotal: number
    originalPrice?: number
    originalTotalPrice?: number
}

export interface CartCouponArticle {
    articleId: string
    quantity: number
}

export interface CartCoupon {
    id: string
    cartActionButtonLabel: string
    active: boolean
    wasSkipped: boolean
    addArticles: CartCouponArticle[]
}

// the response from the server after we updated the cart (CartController)
interface CartUpdateResponse {
    totalPrice: number
    totalPriceWithoutFees: number
    totalPaymentPrice: number
    totalItems: number
    totalTax: number
    totalTaxes: { tax: number; vat: 7 | 19 | number }[]
    totalCannabisAmount: number
    usedBalance: number
    items: ServerItem[]
    coupons: CartCoupon[]
}

export interface CartUpload {
    file: Blob
    url: string | undefined
    selectedAt: Date
}

interface OrderSpecificPickupAuthorizationData {
    firstName: string
    lastName: string
    dateOfBirth: string
    signature: string
}

const STORAGE_KEY = "cann24-cart"

export const useCartStore = defineStore("cart", () => {
    const { post } = useHttpClient()
    const { appConfig } = useShopAppConfig()
    const items = reactive<CartItem[]>([])
    const uploads = reactive<CartUpload[]>([])

    const containsPrescriptionItems = computed(() => items.some(i => i.article.requiresPrescription))

    /**
     * Whether the cart contains at least one liquid (essence or xtracts) article. For
     * public-insurance patients these are the only articles still covered by the Kostenträger,
     * so they alone determine free shipping and the prescription fee
     * (see ShippingCostService / OrderFeeCalculator).
     */
    const containsLiquidItems = computed(() => items.some(i => isLiquidArticleType(i.article.type)))

    const totalItems = computed(() => items.reduce((n, i) => n + i.amount, 0))

    const totalTaxes = ref<CartUpdateResponse["totalTaxes"]>()
    const totalTax = ref<number>()
    const totalCannabisAmount = ref<number>(0)
    const usedBalance = ref<number>()
    const serverItems = reactive<ServerItem[]>([])
    const coupons = reactive<CartCoupon[]>([])
    const paymentMethod = ref<string>()

    const totalPrice = ref<number>()
    const totalPriceWithoutFees = ref<number>()
    const totalPaymentPrice = ref<number>()

    const shippingMethod = ref<string>()
    const prescriptionTransmission = ref<string>()

    const requestCounter = ref(0)

    // Coupons the customer has explicitly de-activated this session switch to
    // manual-only: applyEligibleFreeArticleCoupons() skips them until they are
    // manually re-activated. The "should we auto-apply right now?" context
    // decision lives in the cart view (CartList with isCart), not here — the
    // store stays route- and policy-agnostic.
    const manuallyDeactivatedCouponIds = new Set<string>()

    const shippingAddress = reactive({
        firstName: "",
        lastName: "",
        street: "",
        city: "",
        zip: "",
        deliveryNote: "",
        additionalAddress: "",
    })

    function _getItemFromArticle(article: Article, raiseErrorWhenNotFound = true): CartItem | undefined {
        const item = items.find(i => i.article.id == article.id)
        if (!item && raiseErrorWhenNotFound) {
            throw Error("article not found")
        }

        return item
    }

    watch(appConfig, () => _loadFromLocalStorage(appConfig.value))
    _loadFromLocalStorage(appConfig.value)

    function _loadFromLocalStorage(config: ShopAppConfig) {
        if (config.offline) {
            return
        }

        if (config.user) {
            shippingAddress.firstName = config.user.firstName ?? ""
            shippingAddress.lastName = config.user.lastName ?? ""
            shippingAddress.street = config.user.street ?? ""
            shippingAddress.city = config.user.city ?? ""
            shippingAddress.zip = config.user.zip ?? ""
            shippingAddress.deliveryNote = config.user.deliveryNote ?? ""
            shippingAddress.additionalAddress = config.user.additionalAddress ?? ""
        }

        // reload cart from storage
        let data
        try {
            data = localStorage.getItem(STORAGE_KEY)
        } catch {
            // security exception. ignore
            return
        }

        // restore cart items from local storage
        if (data) {
            // clear cart
            items.splice(0, items.length)

            for (const item of JSON.parse(data)) {
                // find article for stored cart item
                const article = config.articles.find(i => i.id == item.id)

                if (!article) {
                    // article not found (maybe no longer available), ignore it
                    continue
                }
                // article was found, add it to the cart
                addToCart(article, item.amount || 1, false)

                // restore selected sub items (packaging, integra etc)
                for (const subitem of item.subitems ?? []) {
                    if (!subitem.type || !subitem.value) {
                        // invalid data structure, ignore
                        continue
                    }

                    if (!validateSubItem(subitem.type, subitem.value, article.isPreparedType)) {
                        // type or value are not allowed values, ignroe
                        continue
                    }

                    if (!articleSupportsSubItem(article.type ?? "", subitem.type, article.isPreparedType)) {
                        // article does not support this sub item, ignore
                        continue
                    }

                    // add sub item to cart item
                    changeSubitem(article, subitem.type, subitem.value)
                }
            }
            update().catch()
        }
    }

    function removeFromCart(article: Article): void {
        const item = _getItemFromArticle(article)

        if (!item) {
            return
        }

        const articleId = article.id
        if (articleId) {
            // Removing a freebie deactivates the owning coupon(s) — otherwise
            // the next cart/calculate would just re-add the freebie via the
            // AddFreeArticleAction applicator. Routed through the "manual
            // deactivation" set so auto-apply stays off until the customer
            // re-engages the coupon explicitly.
            for (const coupon of coupons) {
                if (!coupon.active) continue
                if (!coupon.addArticles.some(entry => entry.articleId === articleId)) continue
                coupon.active = false
                manuallyDeactivatedCouponIds.add(coupon.id)
            }
        }

        items.splice(items.indexOf(item), 1)
        update().catch()
    }

    function addToCart(article: Article, amount: number, updateState = true): void {
        const articleId = article.id
        if (updateState && articleId) {
            for (const coupon of coupons) {
                if (coupon.addArticles.some(entry => entry.articleId === articleId)) {
                    coupon.active = true
                }
            }
        }

        const item = _getItemFromArticle(article, false)
        const lockedQuantity = getCouponLockedQuantity(article)

        if (lockedQuantity !== undefined && item) {
            item.amount = lockedQuantity

            if (updateState) {
                update().catch()
            }
            return
        }

        if (item) {
            item.amount += amount
        } else {
            items.push({
                article,
                amount: lockedQuantity ?? amount,
                singlePrice: undefined,
                totalPrice: undefined,
                originalPrice: undefined,
                originalTotalPrice: undefined,
                stockStatus: undefined,
                subItems: [],
            })
        }

        if (updateState) {
            update().catch()
        }
    }

    function changeSubitem(article: Article, type: ArticleSubItemType, value: ARTICLE_SUB_ITEM_VALUE) {
        const item = _getItemFromArticle(article)
        if (!item) {
            return
        }

        if (!item.subItems) {
            item.subItems = []
        }

        // remove the sub item type if it already exists
        item.subItems = item.subItems?.filter(i => i.type !== type)
        item.subItems.push({ type, value })

        update().catch()
    }

    function containsArticle(article: Article): boolean {
        return getCartItemForArticle(article) !== undefined
    }

    function getCouponLockedQuantity(article: Article): number | undefined {
        const articleId = article.id
        if (!articleId) {
            return undefined
        }

        let max: number | undefined
        for (const coupon of coupons) {
            if (!coupon.active) continue
            for (const entry of coupon.addArticles) {
                if (entry.articleId === articleId && (max === undefined || entry.quantity > max)) {
                    max = entry.quantity
                }
            }
        }
        return max
    }

    function isCouponLockedArticle(article: Article): boolean {
        return getCouponLockedQuantity(article) !== undefined
    }

    function getCartItemForArticle(article: Article): CartItem | undefined {
        return items.find(i => i.article.id == article.id)
    }

    function isMinimumAmount(article: Article) {
        const item = _getItemFromArticle(article, false)
        if (item && isCouponLockedArticle(article)) {
            return true
        }

        let minimum: number
        if (article.type === "cannabis") {
            minimum = 5
        } else if (article.isPreparedType) {
            //minimum of batch preparation package amounts
            minimum = article.availableBatchPreparationPackageAmounts?.length
                ? Math.min(...article.availableBatchPreparationPackageAmounts)
                : 5
        } else {
            minimum = 1
        }

        return item?.amount === minimum
    }

    function changeAmount(article: Article, newAmount: number): void {
        const item = _getItemFromArticle(article)
        // const minimum = article.type === "cannabis" ? 5 : 1
        // if (newAmount < minimum) {
        //     removeFromCart(article)
        //     return
        // }

        if (!item) {
            return
        }

        const lockedQuantity = getCouponLockedQuantity(article)
        if (lockedQuantity !== undefined) {
            item.amount = lockedQuantity
            update().catch()
            return
        }

        item.amount = Math.max(0, newAmount)
        update().catch()
    }

    async function clear() {
        items.splice(0, items.length)
        uploads.splice(0, uploads.length)
        await update().catch()
    }

    function setCouponActive(couponId: string, active: boolean): void {
        const coupon = coupons.find(item => item.id === couponId)
        if (!coupon) {
            return
        }

        if (active) {
            // Customer re-engaged with this coupon → auto-apply may resume on
            // future transitions.
            manuallyDeactivatedCouponIds.delete(couponId)
        } else {
            // Customer flipped this coupon off → switch it to manual-only until
            // they explicitly turn it back on.
            manuallyDeactivatedCouponIds.add(couponId)
        }

        coupon.active = active
        update().catch()
    }

    /**
     * Activates every free-article coupon that is currently *eligible*:
     *  - has at least one AddFreeArticle entry,
     *  - that freebie article is already in the cart (we never push articles
     *    into the cart unprompted),
     *  - and the customer hasn't manually deactivated it this session.
     *
     * The caller owns the "may we auto-apply right now?" context decision
     * (cart vs. checkout) — only the cart view (CartList with isCart)
     * invokes this.
     */
    function applyEligibleFreeArticleCoupons(): void {
        let changed = false
        for (const coupon of coupons) {
            if (coupon.active) continue
            if (coupon.addArticles.length === 0) continue
            if (manuallyDeactivatedCouponIds.has(coupon.id)) continue

            const hasFreebieInCart = coupon.addArticles.some(entry => {
                const article = appConfig.value.articles.find(a => a.id === entry.articleId)
                return article !== undefined && getCartItemForArticle(article) !== undefined
            })
            if (!hasFreebieInCart) continue

            coupon.active = true
            changed = true
        }

        // If a coupon was auto-applied above, the cart is now locally in
        // sync with the new freebies but the server still has the previous
        // shape (no coupon in requestedCouponIds yet). Fire one follow-up
        // round-trip so totals/discounts/marker line items reflect the new
        // state. Safe against recursion: the next update sees the coupon
        // already in `previousCouponIds`, so no further transition fires.
        if (changed) {
            update().catch()
        }
    }

    async function update(params: { validatePendingOrders?: boolean } = {}): Promise<void> {
        if (!appConfig.value.user) {
            return
        }

        try {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify(
                    items.map(i => {
                        return {
                            id: i.article.id,
                            amount: i.amount,
                            subitems: i.subItems,
                        }
                    }),
                ),
            )
        } catch {
            // security exception. ignore
        }

        // the server side uses normalized order items. split the original/special double item into separate ones
        const postArticles: {
            articleId: string
            amount: number
            packageAmount: number
            subItems: Record<string, string>[]
        }[] = []

        for (const item of items) {
            // invalidate all prices, we will get new ones from the server, this will show spinners where the prices
            // are normally displayed
            item.singlePrice = undefined
            item.totalPrice = undefined
            item.originalPrice = undefined
            item.originalTotalPrice = undefined
            item.subItemsTotal = undefined
            item.stockStatus = "ok" // hide all warnings

            totalPrice.value = undefined
            totalPaymentPrice.value = undefined

            if (item.amount > 0 && (item.article.packageAmount ?? 0) > 0) {
                postArticles.push({
                    articleId: item.article.id!,
                    amount: item.amount,
                    packageAmount: item.article.packageAmount ?? 0,
                    subItems: item.subItems ?? [],
                })
            }
        }

        const sm = shippingMethod.value ?? ""
        const currentRequest = ++requestCounter.value
        const postCoupons = coupons.filter(coupon => coupon.active).map(coupon => coupon.id)

        return post<CartUpdateResponse>("cart/calculate", {
            items: postArticles,
            coupons: postCoupons,
            shippingMethod: sm,
            ...params,
        }).then(r => {
            if (currentRequest !== requestCounter.value) {
                // we did not perform a new request since we started this one. use the result
                return
            }

            // this.totalItems = r.content.totalItems
            totalPrice.value = r.data.totalPrice
            totalPriceWithoutFees.value = r.data.totalPriceWithoutFees
            totalPaymentPrice.value = r.data.totalPaymentPrice
            totalTaxes.value = r.data.totalTaxes
            totalTax.value = r.data.totalTax
            totalCannabisAmount.value = r.data.totalCannabisAmount
            usedBalance.value = r.data.usedBalance

            coupons.splice(0, coupons.length)
            for (const responseCoupon of r.data.coupons ?? []) {
                if (!responseCoupon?.id) {
                    continue
                }

                const addArticles: CartCouponArticle[] = []
                for (const entry of responseCoupon.addArticles ?? []) {
                    if (!entry?.articleId || typeof entry.articleId !== "string") {
                        continue
                    }
                    const quantity =
                        typeof entry.quantity === "number" && Number.isFinite(entry.quantity)
                            ? Math.max(1, Math.round(entry.quantity))
                            : 1
                    addArticles.push({ articleId: entry.articleId, quantity })
                }

                coupons.push({
                    id: responseCoupon.id,
                    cartActionButtonLabel: responseCoupon.cartActionButtonLabel ?? "Coupon",
                    active: responseCoupon.active,
                    wasSkipped: responseCoupon.wasSkipped,
                    addArticles,
                })
            }

            const lockedQuantities = new Map<string, number>()
            for (const coupon of coupons) {
                if (!coupon.active) continue
                for (const entry of coupon.addArticles) {
                    const existing = lockedQuantities.get(entry.articleId) ?? 0
                    if (entry.quantity > existing) {
                        lockedQuantities.set(entry.articleId, entry.quantity)
                    }
                }
            }
            for (const item of items) {
                const articleId = item.article.id
                if (!articleId) continue
                const lockedQuantity = lockedQuantities.get(articleId)
                if (lockedQuantity !== undefined) {
                    item.amount = lockedQuantity
                }
            }

            serverItems.splice(0, serverItems.length)
            for (const responseItem of r.data.items) {
                if (
                    responseItem.type == "fee" ||
                    responseItem.type == "discount" ||
                    responseItem.type == "shipping" ||
                    responseItem.type == "coupon"
                ) {
                    serverItems.push(responseItem)
                } else {
                    const cartItem = items.find(i => i.article.id == responseItem.id)
                    if (!cartItem) continue
                    if (responseItem.packageAmount == cartItem.article.packageAmount) {
                        cartItem.singlePrice = responseItem.singlePrice
                        cartItem.totalPrice = responseItem.totalPrice
                        cartItem.originalPrice = responseItem.originalPrice
                        cartItem.originalTotalPrice = responseItem.originalTotalPrice
                    } else {
                        throw new Error("response from server does not match our local package amounts")
                    }
                    cartItem.tax = responseItem.tax
                    cartItem.stockStatus = responseItem.stockStatus
                    cartItem.subItemsTotal = responseItem.subItemsTotal
                }
            }
        })
    }

    function submitOrder(): Promise<
        AxiosResponse<{
            url: string
            status: string
            orderId: string
        }>
    > {
        const form = new FormData() // form for file upload
        if (orderSpecificPickupAuthorization.value) {
            form.append("orderSpecificPickupAuthorization[firstName]", orderSpecificPickupAuthorization.value.firstName)
            form.append("orderSpecificPickupAuthorization[lastName]", orderSpecificPickupAuthorization.value.lastName)
            form.append(
                "orderSpecificPickupAuthorization[dateOfBirth]",
                orderSpecificPickupAuthorization.value.dateOfBirth,
            )
            form.append("orderSpecificPickupAuthorization[signature]", orderSpecificPickupAuthorization.value.signature)
        }

        clearOrderSpecificPickupAuthorization()

        let i = 0
        for (const item of items) {
            if (item.amount > 0 && item.article.id) {
                form.append("items[" + i + "][articleId]", item.article.id)
                form.append("items[" + i + "][amount]", item.amount.toString())
                form.append("items[" + i + "][packageAmount]", item.article?.packageAmount?.toString() ?? "1")

                let subItemIndex = 0
                for (const subItem of item.subItems ?? []) {
                    form.append("items[" + i + "][subItems][" + subItemIndex + "][type]", subItem.type)
                    form.append("items[" + i + "][subItems][" + subItemIndex + "][value]", subItem.value)
                    subItemIndex++
                }

                i++
            }
        }
        i = 0
        for (const upload of uploads) {
            form.append(`upload[${i++}]`, upload.file)
        }

        i = 0
        for (const coupon of coupons) {
            if (!coupon.active) {
                continue
            }
            form.append(`coupons[${i}]`, coupon.id)
            i++
        }

        form.append("paymentMethod", paymentMethod.value ?? "")

        form.append("shippingMethod", shippingMethod.value ?? "")
        for (const [key, value] of Object.entries(shippingAddress)) {
            form.append("shippingAddress[" + key + "]", value)
        }

        if (prescriptionTransmission.value) {
            form.append("prescriptionTransmission", prescriptionTransmission.value)
        }

        return post("checkout/order", form)
    }

    const allSubitemsSelected = computed(() => {
        for (const item of items) {
            if (
                articleSupportsSubItem(item.article.type ?? "", "packaging", item.article.isPreparedType) &&
                !item.subItems?.some(i => i.type == "packaging")
            ) {
                return false
            }

            if (
                articleSupportsSubItem(item.article.type ?? "", "humidity", item.article.isPreparedType) &&
                !item.subItems?.some(i => i.type == "humidity")
            ) {
                return false
            }
        }
        return true
    })

    /*─────────────────────────────────────┐
    │  Pickup Authorizations               │
    └─────────────────────────────────────*/
    const orderSpecificPickupAuthorization = ref<OrderSpecificPickupAuthorizationData | null>(null)
    function setOrderSpecificPickupAuthorization(data: OrderSpecificPickupAuthorizationData | null) {
        orderSpecificPickupAuthorization.value = data
    }

    function clearOrderSpecificPickupAuthorization() {
        orderSpecificPickupAuthorization.value = null
    }

    return {
        items,
        uploads,
        addToCart,
        changeSubitem,
        containsArticle,
        isCouponLockedArticle,
        getCartItemForArticle,
        isMinimumAmount,
        changeAmount,
        clear,
        containsPrescriptionItems,
        containsLiquidItems,
        totalItems,
        totalTaxes,
        totalTax,
        totalCannabisAmount,
        usedBalance,
        serverItems,
        coupons,
        totalPrice,
        totalPriceWithoutFees,
        totalPaymentPrice,
        shippingMethod,
        shippingAddress,
        update,
        removeFromCart,
        setCouponActive,
        applyEligibleFreeArticleCoupons,
        paymentMethod,
        orderSpecificPickupAuthorization,
        setOrderSpecificPickupAuthorization,
        submitOrder,
        prescriptionTransmission,
        allSubitemsSelected,
    }
})
